import * as React from "react";
import {gql} from "@apollo/client";
import {query} from "../../lib/apollo";
import Loading from "../loading";
import {
    Alert,
    Box,
    CircularProgress,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableFooter,
    TableHead,
} from "@mui/material";
import DateRange, {RangeToTimes} from "../DateRange";
import moment from "moment";
import TableHeaderRow from "../TableHeaderRow";
import StripedTableRow from "../StripedTableRow";
import TableFooterRow from "../TableFooterRow";
import MultiSelect from "../multiSelect/multiSelect";
import {searchPubs} from "../../pages/blockedPublishers/blockedPublishersService";
import CachedIcon from "@mui/icons-material/Cached";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import IndeterminateCheckBoxOutlinedIcon from "@mui/icons-material/IndeterminateCheckBoxOutlined";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import Timeseries from "../timeseries";
import {toast} from "react-toastify";
import downloadCsv from "../../lib/downloadCsv";

const COLORS = [
    "#b20000", "#a0607e", "#a0609c", "#6960a0", "#5f7aa8", "#5f97a8", "#0087b0",
    "#01b1a5", "#7ab294", "#00b218", "#000000", "#6d6d6d", "#6cb103", "#99b273",
    "#a9b273", "#95b200", "#b29b00", "#b27d00", "#b19b66", "#b18966", "#b25300",
];

function pct(n: number | null | undefined): string {
    if (n == null || Number.isNaN(n)) {
        return "-";
    }
    return `${Number(n).toLocaleString(undefined, {maximumFractionDigits: 1, minimumFractionDigits: 1})}%`;
}

function dateFormatForRange(date: any[]) {
    const days = moment(date[1]).diff(moment(date[0]), "days");
    if (days < 3) {
        return "M/D ha";
    }
    if (days < 70) {
        return "M/D";
    }
    return "MMM YYYY";
}

function mergeReasons(selected: any[], others: any[]) {
    const total = (selected || []).reduce((s: number, x: any) => s + Number(x.count || 0), 0);
    const leads = selected?.[0] ? Number(selected[0].leads || 0) : 0;
    const rows = (selected || []).map((x: any) => ({
        reason: x.reason,
        count: Number(x.count || 0),
        percent: total ? x.count / total * 100 : 0,
        leads,
        leads_percent: leads ? x.count / leads * 100 : null,
        other: 0,
        leads_other: 0,
        other_percent: 0,
        other_leads_percent: null as number | null,
    }));

    let otherTotal = 0;
    let otherLeads = 0;
    for (const x of others || []) {
        otherTotal += Number(x.count || 0);
        otherLeads = Number(x.leads || 0);
        let row = rows.find((y: any) => y.reason === x.reason);
        if (row) {
            row.other = Number(x.count || 0);
            row.leads_other = Number(x.leads || 0);
        } else {
            rows.push({
                reason: x.reason,
                count: 0,
                percent: 0,
                leads,
                leads_percent: null,
                other: Number(x.count || 0),
                leads_other: Number(x.leads || 0),
                other_percent: 0,
                other_leads_percent: null,
            });
        }
    }

    for (const row of rows) {
        row.other_percent = otherTotal ? row.other / otherTotal * 100 : 0;
        row.other_leads_percent = row.leads_other ? row.other / row.leads_other * 100 : null;
    }

    return {
        rows,
        total,
        leads,
        leads_percent: leads ? total / leads * 100 : null,
        otherTotal,
        otherLeads,
        otherLeadsPercent: otherLeads ? otherTotal / otherLeads * 100 : null,
    };
}

function toChart(ts: any) {
    const series: any[] = [];
    let colIdx = 0;
    let maxEpc = 0;
    series.push({
        name: "EPC",
        data: (ts.epc || []).map((p: any) => {
            if (p.value > maxEpc) {
                maxEpc = p.value;
            }
            return [p.timestamp, p.value];
        }),
        visible: false,
        yAxis: 1,
        dashStyle: "ShortDashDotDot",
        marker: {enabled: false},
        color: COLORS[colIdx++],
    });

    const addGroup = (items: any[], prefix: string, dashStyle?: string) => {
        for (const s of items || []) {
            series.push({
                name: prefix + s.label,
                visible: false,
                dashStyle,
                marker: {enabled: false},
                color: COLORS[colIdx++ % COLORS.length],
                data: (s.points || []).map((p: any) => [p.timestamp, p.value]),
            });
        }
    };

    addGroup(ts.series || [], "");
    addGroup(ts.others || [], "Other Pubs: ", "ShortDash");

    return {
        series,
        yAxisRight: {
            title: {text: "EPC"},
            min: 0,
            max: Math.ceil(maxEpc * 1.1) || 6,
            opposite: true,
        },
    };
}

export default function PubCfRedirect(params: {pubId: number, cfRedirect: any, setCfRedirect: any}) {
    React.useEffect(() => {
        if (!params.cfRedirect.dateRange.length) {
            load(RangeToTimes("Today"), params.cfRedirect.selectedPublishers);
        }
    }, []);

    const load = async (date: any, publisherSel = params.cfRedirect.selectedPublishers) => {
        const pubIds = (publisherSel || []).map((x: any) => x.id);
        if (!pubIds.length && params.pubId) {
            pubIds.push(params.pubId);
        }
        if (!pubIds.length) {
            toast.error("Select at least one publisher");
            return;
        }

        params.setCfRedirect({
            ...params.cfRedirect,
            loading: true,
            dateRange: date,
            selectedPublishers: publisherSel,
        });

        const startDate = moment(date[0]).format("YYYY-MM-DD HH:mm:ss");
        const endDate = moment(date[1]).format("YYYY-MM-DD HH:mm:ss");

        const reasonsQ = gql`
            query q($input: ClickFeedPubRedirectInput!) {
                clickFeedPubReasons(input: $input) {
                    selected { reason count leads }
                    others { reason count leads }
                }
            }
        `;
        const chartQ = gql`
            query q($input: ClickFeedPubRedirectInput!) {
                clickFeedPubRedirectTimeseries(input: $input) {
                    epc { timestamp value }
                    series { label points { timestamp value } }
                    others { label points { timestamp value } }
                }
            }
        `;

        const [reasonsRes, chartRes] = await Promise.all([
            query(reasonsQ, {input: {startDate, endDate, publisherIds: pubIds}}),
            query(chartQ, {input: {startDate, endDate, publisherIds: pubIds}}),
        ]);

        const merged = mergeReasons(
            reasonsRes?.clickFeedPubReasons?.selected || [],
            reasonsRes?.clickFeedPubReasons?.others || [],
        );
        const ts = chartRes?.clickFeedPubRedirectTimeseries || {epc: [], series: [], others: []};
        const chart = toChart(ts);

        params.setCfRedirect({
            ...params.cfRedirect,
            loading: false,
            dateRange: date,
            selectedPublishers: publisherSel,
            data: merged,
            series: chart.series,
            yAxisRight: chart.yAxisRight,
            dateFormat: dateFormatForRange(date),
            expanded: {},
        });
    };

    const setSelectedPublishers = (items: any[]) => {
        params.setCfRedirect({...params.cfRedirect, selectedPublishers: items});
    };

    const toggle = async (reason: string) => {
        const current = params.cfRedirect.expanded?.[reason];
        if (current?.open) {
            params.setCfRedirect({
                ...params.cfRedirect,
                expanded: {...params.cfRedirect.expanded, [reason]: {...current, open: false}},
            });
            return;
        }

        params.setCfRedirect({
            ...params.cfRedirect,
            expanded: {
                ...(params.cfRedirect.expanded || {}),
                [reason]: {open: true, loading: true, data: current?.data || []},
            },
        });

        const pubIds = (params.cfRedirect.selectedPublishers || []).map((x: any) => x.id);
        if (!pubIds.length && params.pubId) {
            pubIds.push(params.pubId);
        }
        const date = params.cfRedirect.dateRange;
        const q = gql`
            query q($input: ClickFeedPubReasonKeywordsInput!) {
                clickFeedPubReasonKeywords(input: $input) {
                    keyword
                    count
                    leads
                }
            }
        `;
        const res = await query(q, {
            input: {
                startDate: moment(date[0]).format("YYYY-MM-DD HH:mm:ss"),
                endDate: moment(date[1]).format("YYYY-MM-DD HH:mm:ss"),
                publisherIds: pubIds,
                reason,
            },
        });

        params.setCfRedirect((prev: any) => {
            const state = prev || params.cfRedirect;
            return {
                ...state,
                expanded: {
                    ...(state.expanded || {}),
                    [reason]: {open: true, loading: false, data: res?.clickFeedPubReasonKeywords || []},
                },
            };
        });
    };

    const exportKeywords = (reason: string, data: any[], count: number) => {
        const content: any[][] = [["Keyword", "Count", "Percent", "Leads", "Leads Percent"]];
        (data || []).forEach((r: any) => {
            content.push([
                r.keyword,
                r.count,
                Math.round(r.count / (count || 1) * 1000) / 10,
                r.leads,
                r.leads ? Math.round(r.count / r.leads * 1000) / 10 : 9999,
            ]);
        });
        const filename = `${String(reason).replace(/[^a-zA-Z0-9 ]/g, "").replace(/ /g, "_")}.csv`;
        downloadCsv(filename, content);
    };

    if (params.cfRedirect.loading || !params.cfRedirect.dateRange.length) {
        return <Box sx={{padding: 3}}><Loading/></Box>;
    }

    const rows = params.cfRedirect.data?.rows || [];
    const totals = params.cfRedirect.data || {total: 0, leads: 0, otherTotal: 0, otherLeads: 0};

    return (
        <Box sx={{padding: "0 10px 10px 10px"}}>
            <Box sx={{display: "flex", flexDirection: "row", margin: 0, padding: 1, flexFlow: "wrap", alignItems: "flex-end"}}>
                <DateRange
                    showTime={true}
                    dateRange={params.cfRedirect.dateRange}
                    setDateRange={(value: any) => load(value)}
                    style={{marginBottom: "15px", marginRight: "10px"}}
                />
                <Box sx={{width: "350px", marginLeft: "auto", display: "flex"}}>
                    <MultiSelect
                        searchFunction={searchPubs}
                        selectedItems={params.cfRedirect.selectedPublishers || []}
                        setSelectedItems={setSelectedPublishers}
                        placeholder={"Select Publishers"}
                    />
                    <CachedIcon
                        sx={{cursor: "pointer", margin: "5px 0 0 5px"}}
                        onClick={() => load(params.cfRedirect.dateRange)}
                        color={"success"}
                    />
                </Box>
            </Box>

            {!rows.length ? <Alert severity="warning">No results found.</Alert> :
                <TableContainer component={Paper} sx={{maxWidth: "calc(100vw - 20px)"}}>
                    <Table size="small">
                        <TableHead>
                            <TableHeaderRow>
                                <TableCell sx={{width: 40}}/>
                                <TableCell>Reason</TableCell>
                                <TableCell align="right">Count</TableCell>
                                <TableCell align="right">%</TableCell>
                                <TableCell align="right">Leads</TableCell>
                                <TableCell align="right">Leads %</TableCell>
                                <TableCell align="right">Other Pubs</TableCell>
                                <TableCell align="right">Other Pubs %</TableCell>
                                <TableCell align="right">Other Pubs Leads</TableCell>
                                <TableCell align="right">Other Pubs Leads %</TableCell>
                            </TableHeaderRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((row: any) => {
                                const exp = params.cfRedirect.expanded?.[row.reason];
                                return (
                                    <React.Fragment key={row.reason}>
                                        <StripedTableRow>
                                            <TableCell>
                                                <IconButton size="small" onClick={() => toggle(row.reason)}>
                                                    {exp?.open
                                                        ? <IndeterminateCheckBoxOutlinedIcon sx={{fontSize: "20px"}}/>
                                                        : <AddBoxOutlinedIcon sx={{fontSize: "20px"}}/>}
                                                </IconButton>
                                            </TableCell>
                                            <TableCell>{row.reason}</TableCell>
                                            <TableCell align="right">{Number(row.count).toLocaleString()}</TableCell>
                                            <TableCell align="right">{pct(row.percent)}</TableCell>
                                            <TableCell align="right">{Number(row.leads).toLocaleString()}</TableCell>
                                            <TableCell align="right">{pct(row.leads_percent)}</TableCell>
                                            <TableCell align="right">{Number(row.other || 0).toLocaleString()}</TableCell>
                                            <TableCell align="right">{pct(row.other_percent)}</TableCell>
                                            <TableCell align="right">{Number(row.leads_other || 0).toLocaleString()}</TableCell>
                                            <TableCell align="right">{pct(row.other_leads_percent)}</TableCell>
                                        </StripedTableRow>
                                        {exp?.open && (
                                            <StripedTableRow>
                                                <TableCell colSpan={10} sx={{padding: 0}}>
                                                    {exp.loading ? (
                                                        <Box sx={{display: "flex", justifyContent: "center", padding: 2}}>
                                                            <CircularProgress size={28}/>
                                                        </Box>
                                                    ) : !(exp.data || []).length ? (
                                                        <Box sx={{textAlign: "center", padding: 2}}>No Results</Box>
                                                    ) : (
                                                        <Table size="small">
                                                            <TableHead>
                                                                <TableHeaderRow>
                                                                    <TableCell>
                                                                        Keyword
                                                                        <FileDownloadIcon
                                                                            sx={{fontSize: "18px", cursor: "pointer", marginLeft: "6px", verticalAlign: "middle"}}
                                                                            onClick={() => exportKeywords(row.reason, exp.data, row.count)}
                                                                        />
                                                                    </TableCell>
                                                                    <TableCell align="right">Count</TableCell>
                                                                    <TableCell align="right">%</TableCell>
                                                                    <TableCell align="right">Leads</TableCell>
                                                                    <TableCell align="right">Leads %</TableCell>
                                                                </TableHeaderRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {exp.data.map((kw: any) => (
                                                                    <StripedTableRow key={kw.keyword}>
                                                                        <TableCell>{kw.keyword}</TableCell>
                                                                        <TableCell align="right">{Number(kw.count).toLocaleString()}</TableCell>
                                                                        <TableCell align="right">{pct(row.count ? kw.count / row.count * 100 : 0)}</TableCell>
                                                                        <TableCell align="right">{Number(kw.leads).toLocaleString()}</TableCell>
                                                                        <TableCell align="right">{pct(kw.leads ? kw.count / kw.leads * 100 : null)}</TableCell>
                                                                    </StripedTableRow>
                                                                ))}
                                                            </TableBody>
                                                        </Table>
                                                    )}
                                                </TableCell>
                                            </StripedTableRow>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </TableBody>
                        <TableFooter>
                            <TableFooterRow>
                                <TableCell/>
                                <TableCell>Total</TableCell>
                                <TableCell align="right">{Number(totals.total || 0).toLocaleString()}</TableCell>
                                <TableCell align="right">100.0%</TableCell>
                                <TableCell align="right">{Number(totals.leads || 0).toLocaleString()}</TableCell>
                                <TableCell align="right">{pct(totals.leads_percent)}</TableCell>
                                <TableCell align="right">{Number(totals.otherTotal || 0).toLocaleString()}</TableCell>
                                <TableCell align="right">100.0%</TableCell>
                                <TableCell align="right">{Number(totals.otherLeads || 0).toLocaleString()}</TableCell>
                                <TableCell align="right">{pct(totals.otherLeadsPercent)}</TableCell>
                            </TableFooterRow>
                        </TableFooter>
                    </Table>
                </TableContainer>
            }

            {!!params.cfRedirect.series?.length && (
                <Box sx={{marginTop: 2}}>
                    <Timeseries
                        height={300}
                        series={params.cfRedirect.series}
                        title="CF Reasons"
                        prefix=""
                        suffix="%"
                        dateFormat={params.cfRedirect.dateFormat || "M/D"}
                        decimalPlaces={0}
                        yAxisRight={params.cfRedirect.yAxisRight}
                    />
                </Box>
            )}
        </Box>
    );
}
